import { useEffect, useState } from 'react'
import { Handle, Position } from '@xyflow/react'
import { isFired } from './useFiredFlash'
import { convorcherAdmin } from '../../lib/convorcherAdmin.js'
import { normalizeStoryList, storySelectOptions } from '../../lib/storyStore.js'
import { characterSelectOptions } from '../../lib/brainVoiceStore.js'
import { SESSION_FEATURES, hasFeature, toggleFeature, unknownFeatures } from '../../lib/sessionFeatures.js'

/**
 * Convorcher / AvatarOS Whisper event nodes.
 * Each maps to a Unreal FAvatarOsTags gameplay tag broadcast.
 *
 * Exec handle is inside the header div so it sits at header midpoint,
 * not at the node's full vertical center.
 *
 * Data pins (round, typed) let artists wire field values to downstream nodes.
 */

const HEADER_COLOR  = '#7a3010'
const ACCENT_COLOR  = '#ff7040'
const BORDER_ACTIVE = '#d04020'
const BORDER_IDLE   = '#5a2008'

// Data pin type colors (Unreal Blueprint conventions)
const PIN_STRING = '#c060e8'   // purple  — string
const PIN_INT    = '#80c8e8'   // teal    — int
const PIN_BOOL   = '#d06060'   // red     — bool
const PIN_NAME   = '#90d890'   // green   — name / identifier

function DataPinRow({ id, label, color }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
      padding: '3px 22px 3px 12px',
      borderBottom: '1px solid #110a06',
      position: 'relative',
    }}>
      <span style={{ color: '#5a3020', fontSize: 9 }}>{label}</span>
      <Handle
        type="source"
        position={Position.Right}
        id={id}
        style={{ background: color, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${color}66` }}
      />
    </div>
  )
}

function PayloadRow({ label, value }) {
  if (value === undefined || value === null) return null
  return (
    <div style={{ display: 'flex', gap: 6, fontSize: 10 }}>
      <span style={{ color: '#4a3028' }}>{label}:</span>
      <span style={{ color: '#806050' }}>{String(value)}</span>
    </div>
  )
}

function EventHeader({ label, icon }) {
  return (
    <div style={{
      background: HEADER_COLOR, padding: '6px 12px',
      display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
    }}>
      <span style={{ fontSize: 13 }}>{icon}</span>
      <span style={{ color: ACCENT_COLOR, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>
        {label}
      </span>
      <Handle
        type="source"
        position={Position.Right}
        id="execOut"
        style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }}
      />
    </div>
  )
}

function EventShell({ selected, data, label, icon, children }) {
  const fired = isFired(data)
  return (
    <div style={{
      background: selected ? '#200e08' : '#140808',
      border: `2px solid ${fired ? '#ffcc44' : selected ? BORDER_ACTIVE : BORDER_IDLE}`,
      borderRadius: 6, minWidth: 210, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : selected ? '0 0 12px rgba(255,100,40,0.2)' : 'none',
    }}>
      <EventHeader label={label} icon={icon} />
      {children}
    </div>
  )
}

// ── 0. On WS Connected ───────────────────────────────────────────────────────
export function WSConnectedNode({ selected, data }) {
  return (
    <EventShell selected={selected} data={data} label="On WS Connected" icon="🟢">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10 }}>
        WebSocket connection established.
      </div>
    </EventShell>
  )
}

// ── 1. On WS Disconnected ─────────────────────────────────────────────────────
export function WSDisconnectedNode({ selected, data }) {
  const d = data?._debugEventData
  return (
    <EventShell selected={selected} data={data} label="On WS Disconnected" icon="🔴">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10, borderBottom: '1px solid #110a06' }}>
        WebSocket closed.
        {d && (
          <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <PayloadRow label="code"   value={d.code} />
            <PayloadRow label="reason" value={d.reason} />
          </div>
        )}
      </div>
      <DataPinRow id="out-code"   label="code"   color={PIN_INT}    />
      <DataPinRow id="out-reason" label="reason" color={PIN_STRING} />
    </EventShell>
  )
}

// ── 3a. On New Response ───────────────────────────────────────────────────────
// Standard Convorcher response (audio + animation streamed from server).
// Fires when a NewResponse arrives WITHOUT a prefab/trigger tag.
export function NewResponseNode({ selected, data }) {
  return (
    <EventShell selected={selected} data={data} label="On New Response" icon="💬">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10, borderBottom: '1px solid #110a06' }}>
        Standard streamed response (no prefab trigger).
      </div>
    </EventShell>
  )
}

// ── 3b. On Prefab Trigger ─────────────────────────────────────────────────────
export function PrefabTriggerNode({ selected, data }) {
  const d = data?._debugEventData
  return (
    <EventShell selected={selected} data={data} label="On Prefab Trigger" icon="🎬">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10, borderBottom: '1px solid #110a06' }}>
        Fires when a prefab/animation trigger arrives.
        {d && (
          <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <PayloadRow label="animName" value={d.animName} />
          </div>
        )}
      </div>
      <DataPinRow id="out-animName" label="animName" color={PIN_NAME}   />
      <DataPinRow id="out-prefab"   label="prefab"   color={PIN_STRING} />
    </EventShell>
  )
}

// ── 4. On Response Complete ───────────────────────────────────────────────────
// VoiceText.Data.AllProcessed — all data received (not necessarily all played)
export function ResponseCompleteNode({ selected, data }) {
  const d = data?._debugEventData
  return (
    <EventShell selected={selected} data={data} label="On Response Complete" icon="📦">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10, borderBottom: '1px solid #110a06' }}>
        All sentence data received from server.
        {d && (
          <div style={{ marginTop: 4 }}>
            <PayloadRow label="sentences" value={d.sentenceCount} />
          </div>
        )}
      </div>
      <DataPinRow id="out-sentenceCount" label="sentenceCount" color={PIN_INT} />
    </EventShell>
  )
}

// ── 5. On Gesture ─────────────────────────────────────────────────────────────
// VoiceText.Animation — sentence arrived with a gesture tag from the server
export function OnGestureNode({ selected, data }) {
  const d = data?._debugEventData
  return (
    <EventShell selected={selected} data={data} label="On Gesture" icon="🎭">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10, borderBottom: '1px solid #110a06' }}>
        Sentence carries a gesture tag.
        {d && (
          <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <PayloadRow label="anim"   value={d.animName} />
            <PayloadRow label="action" value={d.actionText?.slice(0, 30)} />
          </div>
        )}
      </div>
      <DataPinRow id="out-animName"   label="animName"   color={PIN_NAME}   />
      <DataPinRow id="out-actionText" label="actionText" color={PIN_STRING} />
      <DataPinRow id="out-sentenceNum" label="sentenceNum" color={PIN_INT}  />
    </EventShell>
  )
}

// ── 6. On Sentence Started ────────────────────────────────────────────────────
// VoiceText.Audio.Started
export function OnSentenceStartedNode({ selected, data }) {
  const d = data?._debugEventData
  return (
    <EventShell selected={selected} data={data} label="On Sentence Started" icon="▶">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10, borderBottom: '1px solid #110a06' }}>
        A sentence begins playing.
        {d && (
          <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <PayloadRow label="sentence" value={d.sentenceNum} />
            <PayloadRow label="text" value={d.text ? `"${d.text.slice(0, 32)}…"` : undefined} />
            <PayloadRow label="anim" value={d.animName} />
          </div>
        )}
      </div>
      <DataPinRow id="out-sentenceNum" label="sentenceNum" color={PIN_INT}    />
      <DataPinRow id="out-text"        label="text"        color={PIN_STRING} />
      <DataPinRow id="out-animName"    label="animName"    color={PIN_NAME}   />
      <DataPinRow id="out-actionText"  label="actionText"  color={PIN_STRING} />
    </EventShell>
  )
}

// ── 6b. On Sentence Data Ready ────────────────────────────────────────────────
// Fires once per sentence, when it is READY TO PLAY: audio has landed
// (`terminated`) AND its TextOut has arrived (`hasMeta`). Per sentence and out
// of order — sentence 2 can fire before sentence 1. Ordering/play mapping is
// the consumer's job.
export function OnSentenceDataReadyNode({ selected, data }) {
  const d = data?._debugEventData
  return (
    <EventShell selected={selected} data={data} label="On Sentence Data Ready" icon="📥">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10, borderBottom: '1px solid #110a06' }}>
        A sentence is ready to play (per sentence, may be out of order).
        {d && (
          <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <PayloadRow label="sentence" value={d.sentenceNum} />
            <PayloadRow label="isLast"   value={d.isLast !== undefined ? String(d.isLast) : undefined} />
          </div>
        )}
      </div>
      <DataPinRow id="out-sentenceNum" label="sentenceNum"    color={PIN_INT}  />
      <DataPinRow id="out-isLast"      label="isLastSentence" color={PIN_BOOL} />
    </EventShell>
  )
}

// ── 6c. On First Sentence Ready ───────────────────────────────────────────────
// Fires exactly ONCE per response, when the opening sentence (sentenceNum 0) is
// ready to play — the same terminated + hasMeta AND-join as On Sentence Data
// Ready, filtered to the first sentence. "First" is playback-order first (0),
// not whichever sentence terminates first. Use it to start "avatar begins
// speaking" logic. (On Sentence Data Ready also fires for sentence 0.)
export function OnFirstSentenceReadyNode({ selected, data }) {
  const d = data?._debugEventData
  return (
    <EventShell selected={selected} data={data} label="On First Sentence Ready" icon="🟢">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10, borderBottom: '1px solid #110a06' }}>
        The response's first sentence (0) is ready to play — fires once per response.
        {d && (
          <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <PayloadRow label="sentence" value={d.sentenceNum} />
            <PayloadRow label="isLast"   value={d.isLast !== undefined ? String(d.isLast) : undefined} />
          </div>
        )}
      </div>
      <DataPinRow id="out-sentenceNum" label="sentenceNum"    color={PIN_INT}  />
      <DataPinRow id="out-isLast"      label="isLastSentence" color={PIN_BOOL} />
    </EventShell>
  )
}

// ── 7. On Sentence Completed ──────────────────────────────────────────────────
// VoiceText.Data.SentenceCompleted
export function OnSentenceCompletedNode({ selected, data }) {
  const d = data?._debugEventData
  return (
    <EventShell selected={selected} data={data} label="On Sentence Completed" icon="✔">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10, borderBottom: '1px solid #110a06' }}>
        A sentence finishes playing.
        {d && (
          <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <PayloadRow label="sentence" value={d.sentenceNum} />
            <PayloadRow label="isLast"   value={d.isLast !== undefined ? String(d.isLast) : undefined} />
          </div>
        )}
      </div>
      <DataPinRow id="out-sentenceNum" label="sentenceNum"    color={PIN_INT}  />
      <DataPinRow id="out-isLast"      label="isLastSentence" color={PIN_BOOL} />
    </EventShell>
  )
}

// ── 8. On Last Sentence Started ───────────────────────────────────────────────
// VoiceText.Data.LastSentenceStarted
export function OnLastSentenceStartedNode({ selected, data }) {
  const d = data?._debugEventData
  return (
    <EventShell selected={selected} data={data} label="On Last Sentence Started" icon="🏁">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10, borderBottom: '1px solid #110a06' }}>
        Final sentence begins playing.
        {d && (
          <div style={{ marginTop: 4 }}>
            <PayloadRow label="sentence" value={d.sentenceNum} />
          </div>
        )}
      </div>
      <DataPinRow id="out-sentenceNum" label="sentenceNum" color={PIN_INT} />
    </EventShell>
  )
}

// ── 9. On All Sentences Played ────────────────────────────────────────────────
// VoiceText.Data.LastSentenceCompleted
export function OnAllSentencesPlayedNode({ selected, data }) {
  return (
    <EventShell selected={selected} data={data} label="On All Sentences Played" icon="✅">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10 }}>
        Last sentence has finished playing.
      </div>
    </EventShell>
  )
}

// ── 10. On Text Input ────────────────────────────────────────────────────────
export function OnTextInputNode({ selected, data }) {
  return (
    <EventShell selected={selected} data={data} label="On Text Input" icon="⌨">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10, borderBottom: '1px solid #110a06' }}>
        User submitted text from the input bar.
      </div>
      <DataPinRow id="out-text" label="text" color={PIN_STRING} />
    </EventShell>
  )
}

// ── 11. On Session ──────────────────────────────────────────────────────────
export function OnSessionNode({ selected, data }) {
  const d = data?._debugEventData
  return (
    <EventShell selected={selected} data={data} label="On Session" icon="🔑">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10, borderBottom: '1px solid #110a06' }}>
        Session established with server.
        {d && (
          <div style={{ marginTop: 4 }}>
            <PayloadRow label="id" value={d.sessionId} />
          </div>
        )}
      </div>
      <DataPinRow id="out-sessionId" label="sessionId" color={PIN_STRING} />
      <DataPinRow id="out-features"  label="features"  color={PIN_STRING} />
    </EventShell>
  )
}

// ── 12. On Metrics ──────────────────────────────────────────────────────────
export function OnMetricsNode({ selected, data }) {
  return (
    <EventShell selected={selected} data={data} label="On Metrics" icon="📊">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10 }}>
        Performance metrics received from server.
      </div>
    </EventShell>
  )
}

// ── 13. On User Typing ──────────────────────────────────────────────────────
export function OnUserTypingNode({ selected, data }) {
  return (
    <EventShell selected={selected} data={data} label="On User Typing" icon="✏">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10 }}>
        User started typing in the input bar (interrupt signal).
      </div>
    </EventShell>
  )
}

// ── 14. On Speech Start (Voxy VAD) ──────────────────────────────────────────
export function OnSpeechStartNode({ selected, data }) {
  return (
    <EventShell selected={selected} data={data} label="On Speech Start" icon="🎤">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10, borderBottom: '1px solid #110a06' }}>
        User started speaking (VAD triggered).
      </div>
      <DataPinRow id="out-sequenceId" label="sequenceId" color={PIN_INT} />
    </EventShell>
  )
}

// ── 15. On Speech End (Voxy VAD) ────────────────────────────────────────────
export function OnSpeechEndNode({ selected, data }) {
  const d = data?._debugEventData
  return (
    <EventShell selected={selected} data={data} label="On Speech End" icon="🔇">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10, borderBottom: '1px solid #110a06' }}>
        User stopped speaking (silence timeout).
        {d && (
          <div style={{ marginTop: 4 }}>
            <PayloadRow label="duration" value={d.durationMs != null ? `${d.durationMs}ms` : undefined} />
          </div>
        )}
      </div>
      <DataPinRow id="out-durationMs" label="durationMs"  color={PIN_INT} />
      <DataPinRow id="out-sequenceId" label="sequenceId"  color={PIN_INT} />
    </EventShell>
  )
}

// ── 16. On Barge In (Voxy) ──────────────────────────────────────────────────
export function OnBargeInNode({ selected, data }) {
  return (
    <EventShell selected={selected} data={data} label="On Barge In" icon="🛑">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10, borderBottom: '1px solid #110a06' }}>
        User spoke while avatar was talking (interrupt).
      </div>
      <DataPinRow id="out-sequenceId" label="sequenceId" color={PIN_INT} />
    </EventShell>
  )
}

// ── 17. On Sentence Waiting ─────────────────────────────────────────────────
export function OnSentenceWaitingNode({ selected, data }) {
  return (
    <EventShell selected={selected} data={data} label="On Sentence Waiting" icon="⏳">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10 }}>
        Next sentence queued but frames not ready yet.
      </div>
    </EventShell>
  )
}

// ── 18. On User Typing Cancelled ────────────────────────────────────────────
export function OnUserTypingCancelledNode({ selected, data }) {
  return (
    <EventShell selected={selected} data={data} label="On User Typing Cancelled" icon="↩">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10 }}>
        User deleted all text after typing — restore previous state.
      </div>
    </EventShell>
  )
}

// ── 18. On Server Error ──────────────────────────────────────────────────────
export function OnServerErrorNode({ selected, data }) {
  const d = data?._debugEventData
  return (
    <EventShell selected={selected} data={data} label="On Server Error" icon="⚠">
      <div style={{ padding: '6px 12px', color: '#4a2818', fontSize: 10, borderBottom: '1px solid #110a06' }}>
        Server sent an Error message.
        {d && (
          <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <PayloadRow label="message" value={d.message} />
          </div>
        )}
      </div>
      <DataPinRow id="out-message" label="message" color={PIN_STRING} />
    </EventShell>
  )
}

// ── 19. Convorcher Session (Action) ──────────────────────────────────────────
// Declares the Convorcher session from the graph: which character to converse
// as and which session features to request. Wire from beginPlay. On fire it
// (re)connects the WS with this character + features; the returned session id
// flows out the `session_id` pin (resolved from the session_id constant once
// the server's `session` message lands). This is how a blueprint targets a
// conversational persona distinct from the rendered character — e.g. the
// `isaac-gs` splat character runs a graph whose session node sets
// character = "isaac".
// List fetches for the CHARACTER/STORY dropdowns (ENG-875). One
// in-flight/settled promise per list, shared across mounts; a failed fetch is
// never cached so the next mount (or an explicit Retry) hits the server again.
let _storiesPromise = null
function fetchStories(force = false) {
  if (force || !_storiesPromise) {
    _storiesPromise = convorcherAdmin.listStories().then(normalizeStoryList)
    _storiesPromise.catch(() => { _storiesPromise = null })
  }
  return _storiesPromise
}

let _brainsPromise = null
function fetchBrains(force = false) {
  if (force || !_brainsPromise) {
    _brainsPromise = convorcherAdmin.listBrainCharacters()
      .then(res => (Array.isArray(res) ? res : Array.isArray(res?.result) ? res.result : []))
    _brainsPromise.catch(() => { _brainsPromise = null })
  }
  return _brainsPromise
}

// FetchSelect — a server-sourced dropdown for a session-node field. A fetch
// failure is LOUD (explicit "<noun> unavailable" + Retry), never a silent
// empty list, and a current value missing from the fetched list stays visible
// + selectable via the flagged option `buildOptions` appends. The stored
// value stays a plain string (blueprint schema unchanged).
function FetchSelect({ label, aria, noun, noneLabel, value, onChange, load, buildOptions }) {
  const stop = e => e.stopPropagation()
  const [items, setItems] = useState(null) // null = loading
  const [error, setError] = useState(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let alive = true
    setItems(null)
    setError(null)
    load(attempt > 0).then(
      list => { if (alive) setItems(list) },
      err => { if (alive) setError(String(err?.message ?? err)) },
    )
    return () => { alive = false }
  }, [attempt, load])

  let body
  if (error) {
    body = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: '#f87171', fontSize: 9, flex: 1 }}>{noun} unavailable: {error}</span>
        <button
          onClick={() => setAttempt(a => a + 1)}
          onMouseDown={stop}
          style={{
            background: '#2a1008', border: '1px solid #5a2008', color: '#ff9060',
            borderRadius: 3, fontSize: 9, fontFamily: 'var(--m-font)', padding: '2px 7px', cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    )
  } else if (items === null) {
    body = <div style={{ color: '#5a3020', fontSize: 9 }}>loading {noun}…</div>
  } else {
    body = (
      <select
        className="nodrag"
        aria-label={aria}
        value={value ?? ''}
        onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
        onMouseDown={stop}
        style={{
          width: '100%', boxSizing: 'border-box',
          background: '#0a0402', border: '1px solid #3a1808',
          color: '#ff9060', padding: '4px 5px',
          borderRadius: 3, fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none', cursor: 'pointer',
        }}
      >
        <option value="">{noneLabel}</option>
        {buildOptions(items, value).map(o => (
          <option key={o.value} value={o.value} style={o.missing ? { color: '#f0b050' } : undefined}>
            {o.label}
          </option>
        ))}
      </select>
    )
  }

  return (
    <div style={{ padding: '6px 12px 0 12px' }}>
      <div style={{ color: '#5a3020', fontSize: 9, marginBottom: 3, letterSpacing: 0.5 }}>{label}</div>
      {body}
    </div>
  )
}

// FEATURES checkboxes — the field stays a comma-separated string on
// data.features; the checkboxes cover convorcher's UserFacingFeatures
// whitelist (SESSION_FEATURES), the only tokens the server honors. Tokens
// outside the whitelist are preserved in the string and surfaced as "ignored
// by server", never silently dropped.
function FeaturesField({ value, onChange }) {
  const stop = e => e.stopPropagation()
  const unknown = unknownFeatures(value)
  return (
    <div style={{ padding: '6px 12px 0 12px' }}>
      <div style={{ color: '#5a3020', fontSize: 9, marginBottom: 3, letterSpacing: 0.5 }}>FEATURES</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {SESSION_FEATURES.map(f => (
          <label
            key={f}
            onMouseDown={stop}
            style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#ff9060', fontSize: 10, cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              className="nodrag"
              aria-label={f}
              checked={hasFeature(value, f)}
              onChange={e => onChange(toggleFeature(value, f, e.target.checked))}
              onMouseDown={stop}
              style={{ accentColor: ACCENT_COLOR, cursor: 'pointer' }}
            />
            {f}
          </label>
        ))}
      </div>
      {unknown.length > 0 && (
        <div style={{ color: '#f0b050', fontSize: 9, marginTop: 3 }}>ignored by server: {unknown.join(', ')}</div>
      )}
    </div>
  )
}

// The number convorcher falls back to when a session sends no fast_sentences.
// Mirrors config.DefaultFastSentences (convorcher). Shown as the placeholder so
// an empty box states the value it actually resolves to — "— server default —"
// hid the one number the operator needs. Display only: an empty field still
// sends NOTHING, so the server stays the single source of truth for the default
// and a change there can never silently disagree with what a session gets.
const SERVER_DEFAULT_FAST_SENTENCES = 1

// TTS fast-model switchover (ENG-945). Stored as a string on data.fastSentences
// so "" can mean "not set" — distinct from "0", which means "never use the fast
// model". That distinction is why this cannot default the widget to 0: empty
// resolves to SERVER_DEFAULT_FAST_SENTENCES, not to 0.
function FastSentencesField({ value, onChange }) {
  const stop = e => e.stopPropagation()
  const raw = value ?? ''
  const invalid = raw !== '' && !/^\d+$/.test(String(raw).trim())
  return (
    <div style={{ padding: '6px 12px 0 12px' }}>
      <div style={{ color: '#5a3020', fontSize: 9, marginBottom: 3, letterSpacing: 0.5 }}>
        FAST TTS SENTENCES (optional)
      </div>
      <input
        type="number"
        min="0"
        step="1"
        inputMode="numeric"
        className="nodrag"
        aria-label="Fast TTS sentences"
        placeholder={String(SERVER_DEFAULT_FAST_SENTENCES)}
        value={raw}
        onChange={e => onChange(e.target.value)}
        onMouseDown={stop}
        // A number input still swallows arrow keys / wheel to the graph canvas.
        onKeyDown={stop}
        onWheel={e => e.currentTarget.blur()}
        style={{
          width: '100%', boxSizing: 'border-box',
          background: '#0d0504', color: invalid ? '#f07050' : '#ff9060',
          border: `1px solid ${invalid ? '#f07050' : '#5a3020'}`,
          borderRadius: 3, padding: '3px 6px', fontSize: 10, fontFamily: 'var(--m-mono)',
        }}
      />
      <div style={{ color: invalid ? '#f07050' : '#5a3020', fontSize: 9, marginTop: 3 }}>
        {invalid
          ? 'must be a whole number ≥ 0 — the server will reject this session'
          : `empty = ${SERVER_DEFAULT_FAST_SENTENCES} (server default) · 0 = never use the fast model`}
      </div>
    </div>
  )
}

export function ConvorcherSessionNode({ selected, data }) {
  const fired = isFired(data)
  return (
    <div style={{
      background: selected ? '#200e08' : '#140808',
      border: `2px solid ${fired ? '#ffcc44' : selected ? BORDER_ACTIVE : BORDER_IDLE}`,
      borderRadius: 6, minWidth: 230, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : selected ? '0 0 12px rgba(255,100,40,0.2)' : 'none',
    }}>
      <div style={{
        background: HEADER_COLOR, padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execIn"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
        <span style={{ fontSize: 13 }}>🔌</span>
        <span style={{ color: ACCENT_COLOR, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>
          Convorcher Session
        </span>
        <Handle type="source" position={Position.Right} id="execOut"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
      </div>

      <FetchSelect
        label="CHARACTER (convorcher slug)"
        aria="Character"
        noun="characters"
        noneLabel="— pick character —"
        value={data.character}
        onChange={v => data.onChange?.('character', v)}
        load={fetchBrains}
        buildOptions={characterSelectOptions}
      />
      <FeaturesField
        value={data.features}
        onChange={v => data.onChange?.('features', v)}
      />
      <FetchSelect
        label="STORY (optional)"
        aria="Story"
        noun="stories"
        noneLabel="— none —"
        value={data.story}
        onChange={v => data.onChange?.('story', v)}
        load={fetchStories}
        buildOptions={storySelectOptions}
      />
      <FastSentencesField
        value={data.fastSentences}
        onChange={v => data.onChange?.('fastSentences', v)}
      />

      <div style={{ height: 6 }} />
      <DataPinRow id="out-session_id" label="session_id" color={PIN_STRING} />
    </div>
  )
}
